import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import type * as React from 'react';
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-control)] font-semibold transition-colors disabled:pointer-events-none disabled:opacity-60 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-brand text-brand-ink hover:bg-brand-hover',
        secondary: 'bg-surface text-ink border border-line-strong hover:bg-surface-2',
        ghost: 'text-ink-2 hover:bg-surface-2 hover:text-ink',
        danger: 'bg-danger text-white hover:brightness-110',
        link: 'text-brand underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-10 px-3 text-sm sm:h-9 [&_svg]:size-4',
        md: 'h-10 px-3.5 text-sm sm:h-11 sm:px-4 sm:text-[15px] [&_svg]:size-4',
        lg: 'h-14 px-5 text-base [&_svg]:size-5',
        icon: 'size-11 sm:size-9 [&_svg]:size-4',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Shows a spinner and disables the button. One prop, so no screen reinvents it. */
  loading?: boolean;
}

/**
 * `forwardRef` is load-bearing, not ceremony — the same reason `Input` has it.
 *
 * Radix anchors a floating panel to its trigger by taking a ref to it, and
 * `<PopoverTrigger asChild><Button/></PopoverTrigger>` is how a button opens
 * one. Without this, React 18 drops that ref, the popper is never positioned,
 * and `@radix-ui/react-popper` leaves the panel at its unpositioned fallback:
 *
 *     transform: isPositioned ? floatingStyles.transform : 'translate(0, -200%)'
 *
 * Two hundred percent of its own height above the viewport. The panel mounts,
 * is fully interactive, and is off-screen — which is exactly how the header's
 * notification bell came to "display nothing" when clicked.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild, loading, children, disabled, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  const isDisabled = disabled || loading;

  /**
   * `Slottable` is load-bearing with `asChild`. Slot accepts exactly one child,
   * and the spinner would make two — so `<Button asChild><Link/></Button>`
   * threw "Invalid children" and took the whole page down. Slottable marks
   * which child is the slot target, letting the spinner sit beside it.
   */
  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      // `disabled` is not a valid attribute on an anchor, so a slotted child
      // gets the ARIA form instead — and pointer-events-none already blocks it.
      {...(asChild ? { 'aria-disabled': isDisabled || undefined } : { disabled: isDisabled })}
      {...props}
    >
      {loading && <Loader2 className="animate-spin" aria-hidden="true" />}
      <Slottable>{children}</Slottable>
    </Comp>
  );
});

export { buttonVariants };
